/**
 * The strict Case list URL: an invalid value is removed, and the user is told.
 *
 * Why the notice is recorded in `validateSearch` and not read later from the
 * address bar: on the first load the router writes the validated search back
 * to the URL with `replace` (`Transitioner`) before the page renders, and that
 * new load supersedes the one that saw the raw URL. A `beforeLoad` or a
 * component can see an already-clean URL and never know. `validateSearch`
 * always sees the raw one.
 *
 * Nothing subscribes to the record, so it is a plain module, not a store
 * (P-20, cf. P-22).
 */
import { CASE_FILTER_LABELS } from './case-filter-groups'
import { inspectCaseListSearch, orderCaseListFields } from './case-filters'

import type { CaseListField, CaseListSearch } from './case-filters'

/** The fields the last invalid URL held, until the list shows the notice. */
let pending: Array<CaseListField> = []

/**
 * Both Case list routes' `validateSearch`: the parsed search, with the invalid
 * fields recorded for {@link takeInvalidCaseListFields}.
 *
 * The router also runs this for a valid search (links, the clean rewrite), so
 * only an invalid one writes the record — a valid pass must not erase a notice
 * not shown yet.
 */
export const validateCaseListSearch = (
	raw: Record<string, unknown>,
): CaseListSearch => {
	const { search, invalid } = inspectCaseListSearch(raw)
	if (invalid.length > 0) pending = invalid
	return search
}

/**
 * The recorded invalid fields, once: the record is cleared, so the notice shows
 * a single time. Empty when the URL was valid.
 */
export const takeInvalidCaseListFields = (): Array<CaseListField> => {
	const fields = pending
	pending = []
	return fields
}

const FIELD_LABELS: Record<CaseListField, string> = {
	filters: 'Filters',
	...CASE_FILTER_LABELS,
	openedAfter: 'Opened after',
	openedBefore: 'Opened before',
}

/** "Removed invalid filters: Status, Source." — groups in menu order. */
export const invalidFiltersMessage = (fields: Iterable<CaseListField>) =>
	`Removed invalid filters: ${orderCaseListFields(fields)
		.map((field) => FIELD_LABELS[field])
		.join(', ')}.`
